import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { ConfirmDialog } from '../common/ui/ConfirmDialog';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

const ConfirmDialogContext = createContext(null);

const DEFAULT_STATE = {
  open: false,
  mode: 'confirm',
  title: '',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'primary',
};

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const resolveRef = useRef(null);

  const settle = useCallback((value) => {
    if (resolveRef.current) {
      resolveRef.current(value);
      resolveRef.current = null;
    }
    setState((s) => ({ ...s, open: false }));
  }, []);

  const confirm = useCallback(
    ({
      title = 'Confirm',
      description = '',
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      variant = 'primary',
    } = {}) =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        setState({
          open: true,
          mode: 'confirm',
          title,
          description,
          confirmLabel,
          cancelLabel,
          variant,
        });
      }),
    []
  );

  const alert = useCallback(
    ({
      title = 'Notice',
      description = '',
      confirmLabel = 'OK',
      variant = 'primary',
    } = {}) =>
      new Promise((resolve) => {
        resolveRef.current = () => resolve(true);
        setState({
          open: true,
          mode: 'alert',
          title,
          description,
          confirmLabel,
          cancelLabel: '',
          variant,
        });
      }),
    []
  );

  const handleOpenChange = useCallback(
    (open) => {
      if (!open) settle(false);
    },
    [settle]
  );

  const handleConfirm = useCallback(() => {
    settle(true);
  }, [settle]);

  const icon =
    state.variant === 'destructive' ? AlertTriangle : state.mode === 'alert' ? Info : AlertCircle;

  return (
    <ConfirmDialogContext.Provider value={{ confirm, alert }}>
      {children}
      <ConfirmDialog
        open={state.open}
        onOpenChange={handleOpenChange}
        title={state.title}
        description={state.description}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.mode === 'alert' ? null : state.cancelLabel}
        variant={state.variant}
        icon={icon}
        onConfirm={handleConfirm}
        hideCancel={state.mode === 'alert'}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialogContext() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmDialogContext must be used within ConfirmDialogProvider');
  }
  return ctx;
}

/** Promise-based confirm — replaces window.confirm */
export function useConfirm() {
  return useConfirmDialogContext().confirm;
}

/** Promise-based alert — replaces window.alert */
export function useAlert() {
  return useConfirmDialogContext().alert;
}
